<template>
  <section class="linked-group">
    <header v-if="title" class="linked-group-header">
      <DetailLabel tag="h4">{{ title }} ({{ companies.length }})</DetailLabel>
      <p class="linked-group-hint">{{ hint }}</p>
    </header>

    <article
      v-for="company in companies"
      :key="company.group_key"
      class="linked-company"
      :data-testid="`linked-company-${company.group_key}`"
    >
      <div class="linked-company-head">
        <router-link :to="contactDetailPath(company.contact_email, company.siret)" class="linked-company-name">
          {{ company.nom || 'Établissement non renseigné' }}
        </router-link>
        <Badge
          :variant="FIABILITE[company.reliability]?.variant || 'info'"
          size="sm"
          :data-testid="`linked-company-fiabilite-${company.reliability}`"
        >
          {{ fiabiliteLabel(company) }}
        </Badge>
      </div>

      <p class="linked-company-reason">{{ company.link_reason }}</p>

      <p class="linked-company-meta">
        {{ metaLine(company) }}
      </p>

      <ul v-if="company.formations.length > 0" class="linked-formations">
        <li v-for="formation in company.formations.slice(0, 3)" :key="formation.nom">
          {{ formation.nom }}
          <span class="linked-formation-count">
            · {{ formation.count }} {{ formation.count > 1 ? 'demandes' : 'demande' }}
          </span>
        </li>
      </ul>

      <p v-if="company.contacts.length > 0" class="linked-company-contacts">
        Contacts : {{ contactNames(company) }}
      </p>
    </article>
  </section>
</template>

<script setup>
/**
 * Une liste de sociétés liées, chacune avec sa FIABILITÉ en couleur (retour
 * Figma du 2026-09-11 : « une indication visuelle sur la fiabilité de la
 * recommandation ») et, dessous, la phrase qui dit pourquoi elle est là.
 *
 * Même groupe : certaine sur le SIREN, probable sur l'enseigne. Référence
 * trouvée par l'IA : sa note sur 100, très proche à partir de 90.
 */
import { Badge } from '../ui'
import DetailLabel from '../data/DetailLabel.vue'
import { contactDetailPath } from '../../utils/contactRoute.js'

const FIABILITE = {
  certaine: { label: 'Fiabilité certaine', variant: 'success' },
  probable: { label: 'Fiabilité probable', variant: 'warning' },
  tres_forte: { label: 'Très proche', variant: 'success' },
  forte: { label: 'Proche', variant: 'info' }
}

const fiabiliteLabel = (company) => {
  const label = FIABILITE[company.reliability]?.label || 'Liée'
  return company.score != null ? `${label} · ${company.score}/100` : label
}

defineProps({
  title: {
    type: String,
    default: ''
  },
  hint: {
    type: String,
    default: ''
  },
  companies: {
    type: Array,
    default: () => []
  }
})

const metaLine = (company) => [
  company.ville,
  company.siret ? `SIRET ${company.siret}` : null,
  `${company.quote_count} ${company.quote_count > 1 ? 'demandes' : 'demande'}`
].filter(Boolean).join(' · ')

const contactNames = (company) => company.contacts
  .map(person => [person.prenom, person.nom].filter(Boolean).join(' ').trim() || person.email)
  .join(', ')
</script>

<style scoped>
.linked-group {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-3);
}

.linked-group-header {
  border-bottom: 1px solid var(--color-border-light);
  padding-bottom: var(--spacing-2);
}

/* Le nom du groupe est un `DetailLabel` ; sa phrase d'aide, un texte d'aide. */
.linked-group-hint {
  margin: 0;
  font-size: var(--font-size-xs);
  color: var(--color-text-tertiary);
}

.linked-company {
  padding: var(--spacing-3) var(--spacing-2);
  border-bottom: 1px solid var(--color-border-light);
}

.linked-company:last-of-type {
  border-bottom: 0;
}

.linked-company-head {
  display: flex;
  align-items: center;
  gap: var(--spacing-2);
  flex-wrap: wrap;
}

.linked-company-name {
  font-weight: var(--font-weight-semibold);
  color: var(--color-primary);
  text-decoration: none;
}

.linked-company-name:hover {
  text-decoration: underline;
}

.linked-company-reason {
  margin: 2px 0 0;
  font-size: var(--font-size-sm);
  color: var(--color-text-primary);
}

.linked-company-meta {
  margin: 2px 0 0;
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
}

.linked-formations {
  margin: var(--spacing-2) 0 0;
  padding-left: var(--spacing-4);
  font-size: var(--font-size-xs);
  color: var(--color-text-primary);
}

.linked-formation-count {
  color: var(--color-text-tertiary);
}

.linked-company-contacts {
  margin: var(--spacing-2) 0 0;
  font-size: var(--font-size-xs);
  color: var(--color-text-tertiary);
  overflow-wrap: anywhere;
}
</style>
